/**
 * Sistema di gestione permessi e ruoli
 */

import { ROLES } from './constants';

// Mappa delle sezioni con i ruoli autorizzati
export const SECTION_PERMISSIONS = {
  // Profilo accessibile a qualsiasi ruolo (inclusi Personale, Comitato e ruoli futuri)
  PROFILO: Object.values(ROLES),
  INIZIATIVE: [
    ROLES.ASSOCIATO,
    ROLES.SEGRETERIA,
    ROLES.ADMIN,
  ],
  PRENOTAZIONI:            [ROLES.ASSOCIATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  FORMAZIONE_ONLINE:       [ROLES.ASSOCIATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  FORMAZIONE_ABILITATIVI:  [ROLES.ASSOCIATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  PROGRESSI:         [ROLES.ASSOCIATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  REGOLAMENTO:       [ROLES.ASSOCIATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  FORMAZIONE_GALLERIA: [ROLES.ASSOCIATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  // Area DOCENZE — Programmi Proposti, Iniziative Sostenute, Regolamento
  DOCENZE_PROGRAMMI:   [ROLES.DOCENTE, ROLES.SEGRETERIA, ROLES.ADMIN],
  DOCENZE_INIZIATIVE:  [ROLES.DOCENTE, ROLES.SEGRETERIA, ROLES.ADMIN],
  DOCENZE_REGOLAMENTO: [ROLES.DOCENTE, ROLES.SEGRETERIA, ROLES.ADMIN],
  // Area REFERENTE — Collaboratori, Newsletter, Regolamento, Stato Associato, Assistenza Giuridica
  REFERENTE_COLLABORATORI: [ROLES.REFERENTE, ROLES.SEGRETERIA, ROLES.ADMIN],
  REFERENTE_NEWSLETTER:    [ROLES.REFERENTE, ROLES.SEGRETERIA, ROLES.ADMIN],
  REFERENTE_REGOLAMENTO:   [ROLES.REFERENTE, ROLES.SEGRETERIA, ROLES.ADMIN],
  REFERENTE_STATO:         [ROLES.REFERENTE, ROLES.SEGRETERIA, ROLES.ADMIN],
  REFERENTE_ASSISTENZA:    [ROLES.REFERENTE, ROLES.SEGRETERIA, ROLES.ADMIN],
  DOCUMENTI: [
    ROLES.ASSOCIATO,
    ROLES.SEGRETERIA,
    ROLES.ADMIN,
  ],
  RICERCHE: [ROLES.ASSOCIATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  PUBBLICAZIONI: [ROLES.ASSOCIATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  // Box Ricerche in home dashboard — non governa menu/rotte, solo visibilità del box
  RICERCHE_HOME: [ROLES.ADMIN, ROLES.ASSOCIATO, ROLES.ACQUISTATO, ROLES.SEGRETERIA],
  // Accesso agli articoli: controllato lato server per categoria (search-anutel)
  // Linee Guida area Associato
  ASSOCIATO_REGOLAMENTO: [ROLES.ASSOCIATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  // Area ASSOCIATO PERSONALE — solo Stato Associato (il ruolo Personale non accede ad altro)
  PERSONALE_STATO:          [ROLES.PERSONALE, ROLES.SEGRETERIA, ROLES.ADMIN],
  // Area ACCESSO RISERVATO — Documenti, Ricerche, Videocorsi, Pubblicazioni, Regolamento
  ACCESSO_RISERVATO_DOCUMENTI:   [ROLES.ADMIN, ROLES.ACQUISTATO, ROLES.SEGRETERIA],
  // Archivio documenti nella dashboard (/dashboard/documenti, /dashboard/documento/:id): qualsiasi utente loggato;
  // chi può scaricare lo decide il server (puoScaricare)
  ARCHIVIO_DOCUMENTI: Object.values(ROLES),
  ACCESSO_RISERVATO_RICERCHE:    [ROLES.ADMIN, ROLES.ACQUISTATO, ROLES.SEGRETERIA],
  ACCESSO_RISERVATO_VIDEOCORSI:  [ROLES.ADMIN, ROLES.ACQUISTATO, ROLES.SEGRETERIA],
  ACCESSO_RISERVATO_PUBBLICAZIONI: [ROLES.ADMIN, ROLES.ACQUISTATO, ROLES.SEGRETERIA],
  // Pagina stand-alone /scadenzario (link email): unione di PUBBLICAZIONI e ACCESSO_RISERVATO_PUBBLICAZIONI
  SCADENZARIO: [ROLES.ASSOCIATO, ROLES.ACQUISTATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  ACCESSO_RISERVATO_REGOLAMENTO: [ROLES.ADMIN, ROLES.ACQUISTATO, ROLES.SEGRETERIA],
  // Area COMITATI — Temi Iniziative, Comitati Regionali, Regolamento
  COMITATO_TEMI:        [ROLES.COMITATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  COMITATO_REGIONALI:   [ROLES.COMITATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  COMITATO_REGOLAMENTO: [ROLES.COMITATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  COMITATO_PARTECIPAZIONI: [ROLES.COMITATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  COMITATO_ASSOCIATI:   [ROLES.COMITATO, ROLES.SEGRETERIA, ROLES.ADMIN],
  // Area AMMINISTRAZIONE — Associati, Iniziative, Temi Iniziative, Presenze Organi, Regolamento
  AMMINISTRAZIONE_ASSOCIATI:      [ROLES.AMMINISTRAZIONE, ROLES.SEGRETERIA, ROLES.ADMIN],
  AMMINISTRAZIONE_INIZIATIVE:     [ROLES.AMMINISTRAZIONE, ROLES.SEGRETERIA, ROLES.ADMIN],
  AMMINISTRAZIONE_TEMI:           [ROLES.AMMINISTRAZIONE, ROLES.SEGRETERIA, ROLES.ADMIN],
  AMMINISTRAZIONE_PRESENZE_ORGANI:[ROLES.AMMINISTRAZIONE, ROLES.SEGRETERIA, ROLES.ADMIN],
  AMMINISTRAZIONE_REGOLAMENTO:    [ROLES.AMMINISTRAZIONE, ROLES.SEGRETERIA, ROLES.ADMIN],
  // Pagine degli Organi associativi (Giunta, Consiglio, Revisori): contenuto filtrato per categoria da search-anutel
  AMMINISTRAZIONE_ORGANI_PAGINE:  [ROLES.AMMINISTRAZIONE, ROLES.SEGRETERIA, ROLES.ADMIN],
};

/**
 * Verifica se un utente ha accesso a una sezione
 * @param {string|string[]} userRoles - Ruolo/i dell'utente
 * @param {string[]} allowedRoles - Ruoli autorizzati per la sezione
 * @returns {boolean}
 */
export const hasAccess = (userRoles, allowedRoles) => {
  if (!userRoles || !allowedRoles) return false;

  // Admin ha sempre accesso a tutto
  const rolesArray = Array.isArray(userRoles) ? userRoles : [userRoles];
  if (rolesArray.includes(ROLES.ADMIN)) return true;

  // Verifica se almeno uno dei ruoli dell'utente è tra quelli autorizzati
  return rolesArray.some((role) => allowedRoles.includes(role));
};

/**
 * Verifica se un utente può accedere a una sezione specifica
 * @param {string|string[]} userRoles - Ruolo/i dell'utente
 * @param {string} section - Nome della sezione (es. 'PROFILO', 'INIZIATIVE')
 * @returns {boolean}
 */
export const canAccessSection = (userRoles, section) => {
  const allowedRoles = SECTION_PERMISSIONS[section];
  if (!allowedRoles) return false;

  return hasAccess(userRoles, allowedRoles);
};

/**
 * Ottiene tutte le sezioni accessibili per un utente
 * @param {string|string[]} userRoles - Ruolo/i dell'utente
 * @returns {string[]} Array di nomi sezioni accessibili
 */
export const getAccessibleSections = (userRoles) => {
  return Object.keys(SECTION_PERMISSIONS).filter((section) =>
    canAccessSection(userRoles, section)
  );
};

/**
 * Ottiene il messaggio di errore per accesso negato
 * @param {string} section - Nome della sezione
 * @param {string|string[]} userRoles - Ruolo/i dell'utente
 * @returns {string}
 */
export const getAccessDeniedMessage = (section, userRoles) => {
  const rolesArray = Array.isArray(userRoles) ? userRoles : [userRoles];
  const allowedRoles = SECTION_PERMISSIONS[section] || [];

  return `Accesso negato alla sezione ${section}.
    Il tuo ruolo attuale: ${rolesArray.join(', ')}.
    Ruoli richiesti: ${allowedRoles.join(', ')}.`;
};
